import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useFiles, {
  BASE_DIRECTORIES,
  resolveObjectKey,
  resolveRouterPath,
  removePathLastSection,
} from "files/hooks/useFiles";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { ChevronRight, FolderIcon, Loader2 } from "lucide-react";
import { Directory } from "~/components/ui/directory";
import { OnRowClickParams } from "~/components/ui/file-row";
import useUser from "~/contexts/user";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";

interface CloudFilePickerProps {
  selectedFile: FileInfo | null;
  onSelectFile: (file: FileInfo) => void;
  initialPath?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  loadingPlaceholder?: string;
}

/** Max height sized to match the 12-row loading skeleton (12 * 34px + padding). */
const DIRECTORY_MAX_HEIGHT = 420;

/**
 * Builds breadcrumb segments from the current router path.
 * Each segment has a label (human-readable) and the path to navigate to on click.
 */
const buildBreadcrumbs = (currentPath: string): { label: string; path: string }[] => {
  if (!currentPath) {
    return [];
  }

  const segments = currentPath.split("/").filter(Boolean);
  const crumbs: { label: string; path: string }[] = [];

  for (let i = 0; i < segments.length; i++) {
    const partialPath = segments.slice(0, i + 1).join("/");
    const override = BASE_DIRECTORIES[partialPath];
    crumbs.push({
      label: override?.title ?? segments[i],
      path: partialPath,
    });
  }

  return crumbs;
};

/**
 * Inline cloud storage file picker for the ingestion validation modal.
 * Uses the shadcn Directory component with compact mode so external
 * buckets appear in a single list alongside internal locations.
 */
const CloudFilePicker = ({
  selectedFile,
  onSelectFile,
  initialPath,
  className,
  disabled = false,
  isLoading: externalLoading = false,
  placeholder = "Select source file...",
  loadingPlaceholder = "Loading file information...",
}: CloudFilePickerProps) => {
  const { datasetId } = useParams();
  const user = useUser();
  const [currentPath, setCurrentPath] = useState(initialPath ?? "");
  const hasUserNavigated = useRef(false);

  const navigateTo = useCallback((path: string) => {
    hasUserNavigated.current = true;
    setCurrentPath(path);
  }, []);

  useEffect(() => {
    if (initialPath && !hasUserNavigated.current) {
      setCurrentPath(initialPath);
    }
  }, [initialPath]);

  const objectKey = resolveObjectKey(currentPath, user?.email, "", datasetId ?? "");
  const { data: fileData, isLoading: filesLoading } = useFiles(objectKey, true);
  const files = useMemo(() => fileData?.children ?? [], [fileData?.children]);

  const breadcrumbs = useMemo(() => buildBreadcrumbs(currentPath), [currentPath]);

  const handleRowClick = useCallback(
    ({ fileKey, type, s3Uri }: OnRowClickParams) => {
      if (fileKey.includes("..")) {
        navigateTo(removePathLastSection(currentPath));
        return;
      }

      if (type === FileType.FILE) {
        const matchedFile = files.find((f) => f.key === fileKey);
        onSelectFile({
          key: fileKey,
          type: FileType.FILE,
          size: matchedFile?.size,
          s3_uri: s3Uri ?? matchedFile?.s3_uri,
        });
        trackEvent("file_selected", {
          dataset_id: datasetId,
          source: "cloud_storage",
          file_name: fileKey.split("/").pop(),
          file_size_bytes: matchedFile?.size,
        });
        return;
      }

      const routerPath = resolveRouterPath(fileKey, true, datasetId);
      navigateTo(routerPath);
    },
    [files, onSelectFile, datasetId, navigateTo, currentPath]
  );

  if (externalLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        {loadingPlaceholder}
      </div>
    );
  }

  if (disabled) {
    return <div className="text-sm text-muted-foreground py-2 opacity-50">{placeholder}</div>;
  }

  return (
    <div className={cn("w-full rounded-lg border bg-white", className)}>
      {/* Breadcrumb path indicator */}
      <div className="flex items-center gap-1 px-3 py-2 text-xs text-muted-foreground border-b border-gray-200">
        <button
          type="button"
          onClick={() => navigateTo("")}
          className={cn(
            "flex items-center gap-1 shrink-0",
            currentPath && "hover:text-foreground cursor-pointer"
          )}
        >
          <FolderIcon className="h-3.5 w-3.5 shrink-0" />
          <span>All Locations</span>
        </button>
        {breadcrumbs.map((crumb, i) => {
          const isLast = i === breadcrumbs.length - 1;
          return (
            <span key={crumb.path} className="flex items-center gap-1 min-w-0">
              <ChevronRight className="h-3 w-3 shrink-0 text-gray-400" />
              {isLast ? (
                <span className="truncate font-medium text-foreground">{crumb.label}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => navigateTo(crumb.path)}
                  className="truncate hover:text-foreground cursor-pointer"
                >
                  {crumb.label}
                </button>
              )}
            </span>
          );
        })}
      </div>

      {/* Scrollable directory listing */}
      <div className="overflow-y-auto" style={{ height: DIRECTORY_MAX_HEIGHT }}>
        <Directory
          navigation="in-memory"
          currentPath={currentPath}
          files={files}
          loading={filesLoading}
          onRowClick={handleRowClick}
          selectedFileKey={selectedFile?.key ?? selectedFile?.s3_uri}
          showExternalBuckets
          compact
          hideAllActions
          hideMainLocationsHeader
        />
      </div>
    </div>
  );
};

export default CloudFilePicker;
